<template>
  <div>
    <!-- Summary Stats -->
    <v-row dense class="mb-4">
      <v-col cols="3">
        <div class="text-center">
          <div class="text-h5 font-weight-bold pink--text">
            {{ todayPendingCount }}
          </div>
          <div class="caption grey--text">Pending</div>
        </div>
      </v-col>
      <v-col cols="3">
        <div class="text-center">
          <div
            class="text-h5 font-weight-bold"
            :class="
              overdueCount > 0 ? 'red--text' : 'grey--text text--lighten-1'
            "
          >
            {{ overdueCount }}
          </div>
          <div class="caption grey--text">Overdue</div>
        </div>
      </v-col>
      <v-col cols="3">
        <div class="text-center">
          <div class="text-h5 font-weight-bold green--text">
            {{ todayCompletedCount }}
          </div>
          <div class="caption grey--text">Done Today</div>
        </div>
      </v-col>
      <v-col cols="3">
        <div class="text-center">
          <div class="text-h5 font-weight-bold blue--text">
            {{ upcomingCount }}
          </div>
          <div class="caption grey--text">Upcoming</div>
        </div>
      </v-col>
    </v-row>

    <v-divider class="mb-1"></v-divider>

    <!-- Tabs -->
    <v-tabs v-model="activeTab" color="pink" class="mb-3" @change="onTabChange">
      <v-tab>
        <v-icon left small>mdi-calendar-today</v-icon>
        Today
        <v-badge
          v-if="todayActionCount > 0"
          :content="String(todayActionCount)"
          color="red"
          inline
          class="ml-1"
        />
      </v-tab>
      <v-tab>
        <v-icon left small>mdi-calendar-arrow-right</v-icon>
        Upcoming
      </v-tab>
      <v-tab>
        <v-icon left small>mdi-check-all</v-icon>
        Completed
      </v-tab>
      <v-tab>
        <v-icon left small>mdi-refresh</v-icon>
        Recurring
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <!-- ========== TODAY TAB ========== -->
      <v-tab-item>
        <v-alert
          v-if="overdueCount > 0"
          type="error"
          dense
          outlined
          class="mb-3"
        >
          <strong>{{ overdueCount }}</strong> overdue task(s) from previous days
        </v-alert>

        <div
          v-if="todayDisplayTasks.length === 0"
          class="text-center py-8 grey--text"
        >
          <v-icon large color="grey lighten-1">mdi-check-circle-outline</v-icon>
          <div class="mt-2 body-2">All clear! No tasks for today.</div>
        </div>

        <v-simple-table v-else>
          <thead>
            <tr>
              <th style="width: 100px">Priority</th>
              <th>Task</th>
              <th style="width: 80px">Time</th>
              <th style="width: 110px">Status</th>
              <th style="width: 180px">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in todayDisplayTasks"
              :key="item.id"
              :class="{ 'red lighten-5': isOverdue(item) }"
            >
              <td>
                <v-chip
                  small
                  :color="
                    priorityColor(item.task ? item.task.priority : 'basic')
                  "
                  dark
                >
                  {{ priorityLabel(item.task ? item.task.priority : "basic") }}
                </v-chip>
              </td>
              <td>
                {{ item.task ? item.task.title : "" }}
                <v-chip
                  v-if="item.days_delayed > 0"
                  x-small
                  color="deep-orange"
                  dark
                  class="ml-1"
                  >{{ item.days_delayed }} day{{
                    item.days_delayed > 1 ? "s" : ""
                  }}
                  delayed</v-chip
                >
                <v-chip
                  v-else-if="isOverdue(item)"
                  small
                  color="red"
                  dark
                  class="ml-1"
                  >OVERDUE</v-chip
                >
              </td>
              <td>
                {{ item.task ? item.task.scheduled_time || "-" : "-" }}
              </td>
              <td>
                <v-chip small :color="statusColor(item.status)" dark>
                  {{ statusLabel(item.status) }}
                </v-chip>
              </td>
              <td>
                <div class="d-flex align-center flex-nowrap" style="gap: 2px">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                      <v-btn icon small v-on="on" @click="openDetail(item)">
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>View Details</span>
                  </v-tooltip>

                  <v-tooltip
                    bottom
                    v-if="item.remarks && item.remarks.length > 0"
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn icon small v-on="on">
                        <v-icon small color="orange"
                          >mdi-comment-text-multiple</v-icon
                        >
                      </v-btn>
                    </template>
                    <span>{{ item.remarks.length }} remark(s)</span>
                  </v-tooltip>

                  <v-tooltip
                    bottom
                    v-if="
                      item.status === 'pending' || item.status === 'ongoing'
                    "
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        small
                        v-on="on"
                        color="orange"
                        @click="openUpdateStatus(item)"
                      >
                        <v-icon small>mdi-progress-clock</v-icon>
                      </v-btn>
                    </template>
                    <span>Update Status</span>
                  </v-tooltip>

                  <v-tooltip
                    bottom
                    v-if="
                      item.status === 'pending' ||
                      item.status === 'missed' ||
                      item.status === 'ongoing'
                    "
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        small
                        v-on="on"
                        color="success"
                        @click="openComplete(item)"
                      >
                        <v-icon small>mdi-check-circle</v-icon>
                      </v-btn>
                    </template>
                    <span>Mark as Done</span>
                  </v-tooltip>

                  <v-tooltip
                    bottom
                    v-if="item.status === 'acknowledged' && item.admin_comment"
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn icon small v-on="on">
                        <v-icon small color="info">mdi-comment-text</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ item.admin_comment }}</span>
                  </v-tooltip>
                </div>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-tab-item>

      <!-- ========== UPCOMING TAB ========== -->
      <v-tab-item>
        <div v-if="loadingUpcoming" class="text-center py-8">
          <v-progress-circular indeterminate color="pink" />
        </div>
        <div
          v-else-if="my_upcoming_tasks.length === 0"
          class="text-center py-8 grey--text"
        >
          <v-icon large color="grey lighten-1">mdi-calendar-check</v-icon>
          <div class="mt-2 body-2">No upcoming tasks in the next 7 days.</div>
        </div>
        <div v-else>
          <div
            v-for="(tasks, date) in upcomingGrouped"
            :key="date"
            class="mb-4"
          >
            <div
              class="
                subtitle-2
                grey--text
                text--darken-2
                mb-1
                d-flex
                align-center
              "
            >
              <v-icon small class="mr-1" color="grey darken-1"
                >mdi-calendar</v-icon
              >
              {{ formatDate(date) }}
              <v-chip small class="ml-2" color="grey lighten-3">
                {{ tasks.length }}
              </v-chip>
            </div>
            <v-simple-table dense>
              <tbody>
                <tr v-for="item in tasks" :key="item.id">
                  <td style="width: 90px">
                    <v-chip
                      small
                      :color="
                        priorityColor(item.task ? item.task.priority : 'basic')
                      "
                      dark
                    >
                      {{
                        priorityLabel(item.task ? item.task.priority : "basic")
                      }}
                    </v-chip>
                  </td>
                  <td>{{ item.task ? item.task.title : "" }}</td>
                  <td style="width: 80px" class="caption">
                    {{ item.task ? item.task.scheduled_time || "-" : "-" }}
                  </td>
                  <td style="width: 100px" class="caption">
                    {{ item.task ? item.task.category || "" : "" }}
                  </td>
                  <td style="width: 40px">
                    <v-btn icon x-small @click="openDetail(item)">
                      <v-icon small>mdi-eye</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </div>
      </v-tab-item>

      <!-- ========== COMPLETED TAB ========== -->
      <v-tab-item>
        <div class="d-flex align-center mb-3 mt-1">
          <v-btn-toggle v-model="completedRange" mandatory dense color="pink">
            <v-btn small value="7">Last 7 Days</v-btn>
            <v-btn small value="30">Last 30 Days</v-btn>
          </v-btn-toggle>
          <v-spacer />
          <span class="caption grey--text">
            {{ my_completed_tasks.length }} task(s)
          </span>
        </div>

        <div v-if="loadingCompleted" class="text-center py-8">
          <v-progress-circular indeterminate color="pink" />
        </div>
        <div
          v-else-if="my_completed_tasks.length === 0"
          class="text-center py-8 grey--text"
        >
          <v-icon large color="grey lighten-1">mdi-clipboard-text-clock</v-icon>
          <div class="mt-2 body-2">No completed tasks in this period.</div>
        </div>
        <v-data-table
          v-else
          :items="my_completed_tasks"
          :headers="completedHeaders"
          :items-per-page="10"
          dense
          sort-by="log_date"
          sort-desc
        >
          <template v-slot:item.log_date="{ item }">
            {{ item.log_date ? item.log_date.substr(0, 10) : "" }}
          </template>
          <template v-slot:item.title="{ item }">
            {{ item.task ? item.task.title : "" }}
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip small :color="statusColor(item.status)" dark>
              {{ statusLabel(item.status) }}
            </v-chip>
          </template>
          <template v-slot:item.completion_note="{ item }">
            <span class="d-inline-block text-truncate" style="max-width: 200px">
              {{ item.completion_note || "-" }}
            </span>
          </template>
        </v-data-table>
      </v-tab-item>

      <!-- ========== RECURRING TAB ========== -->
      <v-tab-item>
        <div v-if="loadingRecurring" class="text-center py-8">
          <v-progress-circular indeterminate color="pink" />
        </div>
        <div
          v-else-if="my_recurring_tasks.length === 0"
          class="text-center py-8 grey--text"
        >
          <v-icon large color="grey lighten-1">mdi-refresh</v-icon>
          <div class="mt-2 body-2">No recurring tasks assigned to you.</div>
        </div>
        <v-row v-else dense>
          <v-col cols="6" v-for="task in my_recurring_tasks" :key="task.id">
            <v-card outlined class="pa-3">
              <div class="d-flex align-center mb-1">
                <v-chip
                  small
                  :color="priorityColor(task.priority)"
                  dark
                  class="mr-2"
                >
                  {{ priorityLabel(task.priority) }}
                </v-chip>
                <span class="subtitle-2 text-truncate">{{ task.title }}</span>
              </div>
              <div class="caption grey--text">
                <v-icon x-small class="mr-1">mdi-refresh</v-icon>
                {{ formatRecurrence(task) }}
              </div>
              <div v-if="task.scheduled_time" class="caption grey--text">
                <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                {{ task.scheduled_time }}
              </div>
              <div v-if="task.category" class="caption grey--text">
                <v-icon x-small class="mr-1">mdi-tag</v-icon>
                {{ task.category }}
              </div>
              <div v-if="task.assigned_by" class="caption grey--text">
                <v-icon x-small class="mr-1">mdi-account</v-icon>
                Assigned by
                {{
                  task.assigned_by.full_name
                    ? task.assigned_by.full_name
                    : "Admin"
                }}
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>

    <!-- Dialogs -->
    <task-completion-dialog
      v-model="showCompleteDialog"
      :task="selectedTask"
      @completed="onCompleted"
    />
    <task-detail-dialog v-model="showDetailDialog" :task="detailTask" />
    <task-update-status-dialog
      v-model="showUpdateStatusDialog"
      :task="updateStatusTask"
      @updated="onStatusUpdated"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import TaskCompletionDialog from "./TaskCompletionDialog.vue";
import TaskDetailDialog from "./TaskDetailDialog.vue";
import TaskUpdateStatusDialog from "./TaskUpdateStatusDialog.vue";

export default {
  components: {
    TaskCompletionDialog,
    TaskDetailDialog,
    TaskUpdateStatusDialog,
  },
  data() {
    return {
      activeTab: 0,
      showCompleteDialog: false,
      selectedTask: null,
      showDetailDialog: false,
      detailTask: null,
      showUpdateStatusDialog: false,
      updateStatusTask: null,
      loadingUpcoming: false,
      loadingCompleted: false,
      loadingRecurring: false,
      loadedTabs: { 0: true },
      completedRange: "7",
      completedHeaders: [
        { text: "Date", value: "log_date", width: "110" },
        { text: "Task", value: "title" },
        { text: "Status", value: "status", width: "110" },
        { text: "Note", value: "completion_note" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      my_tasks_today: "reminder/my_tasks_today",
      pending_tasks: "reminder/pending_tasks",
      pending_count: "reminder/pending_count",
      my_upcoming_tasks: "reminder/my_upcoming_tasks",
      my_completed_tasks: "reminder/my_completed_tasks",
      my_recurring_tasks: "reminder/my_recurring_tasks",
    }),
    overdue_list() {
      const today = new Date().toISOString().substr(0, 10);
      return this.pending_tasks.filter(
        (t) => t.log_date && t.log_date.substr(0, 10) < today
      );
    },
    todayDisplayTasks() {
      const todayIds = this.my_tasks_today.map((t) => t.id);
      // For recurring tasks, overdue info is already embedded via days_delayed
      // so only merge non-recurring overdue items that aren't already in today
      const todayTaskIds = this.my_tasks_today
        .filter(
          (t) =>
            t.task &&
            t.task.recurrence_type &&
            t.task.recurrence_type !== "none"
        )
        .map((t) => t.task_id);
      const overdue = this.overdue_list.filter(
        (t) => !todayIds.includes(t.id) && !todayTaskIds.includes(t.task_id)
      );
      return [...overdue, ...this.my_tasks_today];
    },
    todayPendingCount() {
      return this.todayDisplayTasks.filter(
        (t) =>
          t.status === "pending" ||
          t.status === "missed" ||
          t.status === "ongoing"
      ).length;
    },
    todayCompletedCount() {
      return this.my_tasks_today.filter(
        (t) => t.status === "completed" || t.status === "acknowledged"
      ).length;
    },
    overdueCount() {
      // Count days_delayed from today's consolidated recurring tasks
      const todayDelayed = this.my_tasks_today.reduce(
        (sum, t) => sum + (t.days_delayed || 0),
        0
      );
      // Count non-recurring overdue items not already represented in today
      const todayTaskIds = this.my_tasks_today
        .filter(
          (t) =>
            t.task &&
            t.task.recurrence_type &&
            t.task.recurrence_type !== "none"
        )
        .map((t) => t.task_id);
      const nonRecurringOverdue = this.overdue_list.filter(
        (t) => !todayTaskIds.includes(t.task_id)
      ).length;
      return nonRecurringOverdue + todayDelayed;
    },
    todayActionCount() {
      return this.pending_count;
    },
    upcomingCount() {
      return this.my_upcoming_tasks.length;
    },
    upcomingGrouped() {
      const groups = {};
      this.my_upcoming_tasks.forEach((item) => {
        const date = item.log_date ? item.log_date.substr(0, 10) : "Unknown";
        if (!groups[date]) groups[date] = [];
        groups[date].push(item);
      });
      return groups;
    },
  },
  created() {
    const today = new Date().toISOString().substr(0, 10);
    this.get_my_tasks_today(today);
    this.get_pending_tasks();
  },
  watch: {
    completedRange() {
      this.fetchCompleted();
    },
  },
  methods: {
    ...mapActions({
      get_my_tasks_today: "reminder/get_my_tasks_today",
      get_pending_tasks: "reminder/get_pending_tasks",
      get_my_upcoming_tasks: "reminder/get_my_upcoming_tasks",
      get_my_completed_tasks: "reminder/get_my_completed_tasks",
      get_my_recurring_tasks: "reminder/get_my_recurring_tasks",
    }),
    onTabChange(tabIndex) {
      if (this.loadedTabs[tabIndex]) return;
      this.$set(this.loadedTabs, tabIndex, true);
      if (tabIndex === 1) this.fetchUpcoming();
      else if (tabIndex === 2) this.fetchCompleted();
      else if (tabIndex === 3) this.fetchRecurring();
    },
    async fetchUpcoming() {
      this.loadingUpcoming = true;
      try {
        await this.get_my_upcoming_tasks(7);
      } catch (e) {
        /* ignore */
      }
      this.loadingUpcoming = false;
    },
    async fetchCompleted() {
      this.loadingCompleted = true;
      const days = parseInt(this.completedRange) || 7;
      const to = new Date().toISOString().substr(0, 10);
      const from = new Date(Date.now() - days * 86400000)
        .toISOString()
        .substr(0, 10);
      try {
        await this.get_my_completed_tasks({ date_from: from, date_to: to });
      } catch (e) {
        /* ignore */
      }
      this.loadingCompleted = false;
    },
    async fetchRecurring() {
      this.loadingRecurring = true;
      try {
        await this.get_my_recurring_tasks();
      } catch (e) {
        /* ignore */
      }
      this.loadingRecurring = false;
    },
    isOverdue(item) {
      const today = new Date().toISOString().substr(0, 10);
      return (
        item.log_date &&
        item.log_date.substr(0, 10) < today &&
        (item.status === "pending" || item.status === "missed")
      );
    },
    priorityColor(priority) {
      return priority === "very_urgent"
        ? "red"
        : priority === "urgent"
        ? "orange"
        : "blue";
    },
    priorityLabel(priority) {
      return priority === "very_urgent"
        ? "Very Urgent"
        : priority === "urgent"
        ? "Urgent"
        : "Basic";
    },
    statusColor(status) {
      const map = {
        pending: "grey",
        ongoing: "orange",
        completed: "blue",
        acknowledged: "green",
        missed: "red",
      };
      return map[status] || "grey";
    },
    statusLabel(status) {
      const map = {
        pending: "Pending",
        ongoing: "Ongoing",
        completed: "Submitted",
        acknowledged: "Acknowledged",
        missed: "Missed",
      };
      return map[status] || status;
    },
    formatDate(dateStr) {
      const d = new Date(dateStr + "T00:00:00");
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (dateStr === tomorrow.toISOString().substr(0, 10)) return "Tomorrow";
      return d.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    },
    formatRecurrence(task) {
      const dayNames = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      const days = task.recurrence_days || [];
      if (task.recurrence_type === "daily") {
        if (days.length === 0 || days.length === 7) return "Every day";
        return days.map((d) => dayNames[d]).join(", ");
      }
      if (task.recurrence_type === "weekly") {
        return "Weekly on " + days.map((d) => dayNames[d]).join(", ");
      }
      if (task.recurrence_type === "monthly") {
        return "Monthly on day " + days.join(", ");
      }
      return "One-time";
    },
    openDetail(item) {
      this.detailTask = item;
      this.showDetailDialog = true;
    },
    openComplete(item) {
      this.selectedTask = item;
      this.showCompleteDialog = true;
    },
    openUpdateStatus(item) {
      this.updateStatusTask = item;
      this.showUpdateStatusDialog = true;
    },
    onCompleted() {
      const today = new Date().toISOString().substr(0, 10);
      this.get_my_tasks_today(today);
      this.get_pending_tasks();
    },
    onStatusUpdated() {
      const today = new Date().toISOString().substr(0, 10);
      this.get_my_tasks_today(today);
      this.get_pending_tasks();
    },
    async refreshAll() {
      const today = new Date().toISOString().substr(0, 10);
      const days = parseInt(this.completedRange) || 7;
      const to = today;
      const from = new Date(Date.now() - days * 86400000)
        .toISOString()
        .substr(0, 10);
      await Promise.all([
        this.get_my_tasks_today(today),
        this.get_pending_tasks(),
        this.get_my_upcoming_tasks(7),
        this.get_my_completed_tasks({ date_from: from, date_to: to }),
        this.get_my_recurring_tasks(),
      ]);
      // Mark all tabs as loaded since we fetched everything
      this.$set(this.loadedTabs, 1, true);
      this.$set(this.loadedTabs, 2, true);
      this.$set(this.loadedTabs, 3, true);
    },
  },
};
</script>
